import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { DepositPaymentDetails } from '../types';
import apiService from '../services/api';
import { formatManualPaymentMethodLabel } from '../utils/manualPaymentHelpers';

export interface DepositPaymentDetailsModalProps {
  isOpen: boolean;
  appointmentId: string | undefined;
  onClose: () => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDateOnly = (dateString: string) => {
  const parts = String(dateString).slice(0, 10).split('-');
  if (parts.length !== 3) return dateString;
  const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
    <span className="text-sm text-gray-600 shrink-0">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right">{value}</span>
  </div>
);

const DepositPaymentDetailsModal: React.FC<DepositPaymentDetailsModalProps> = ({
  isOpen,
  appointmentId,
  onClose,
}) => {
  const [details, setDetails] = useState<DepositPaymentDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !appointmentId) {
      setDetails(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService
      .getAppointmentDepositPaymentDetails(appointmentId)
      .then((data) => {
        if (cancelled) return;
        setDetails(data);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setDetails(null);
        setError(err?.response?.data?.message || 'Failed to load deposit payment details');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, appointmentId]);

  if (!isOpen) return null;

  const recordedByName =
    details?.recordedByFirstName || details?.recordedByLastName
      ? [details?.recordedByFirstName, details?.recordedByLastName].filter(Boolean).join(' ')
      : null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[2000] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200 overflow-hidden max-h-[calc(100vh-6rem)] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <i className="bi bi-wallet2 text-green-600 text-xl" aria-hidden />
            <span>Deposit Payment Details</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <span className="inline-block w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" aria-hidden />
              Loading deposit payment details…
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          ) : details ? (
            <div className="space-y-1">
              <DetailRow
                label="Payment source"
                value={
                  details.source === 'MANUAL'
                    ? 'Manual (business recorded)'
                    : details.source === 'ONLINE'
                      ? 'Online (customer payment)'
                      : 'Recorded deposit'
                }
              />
              <DetailRow label="Amount" value={`$${Number(details.amount).toFixed(2)}`} />

              {details.source === 'MANUAL' && (
                <>
                  <DetailRow
                    label="Payment method"
                    value={formatManualPaymentMethodLabel(details.paymentMethod)}
                  />
                  {details.paymentDate ? (
                    <DetailRow label="Payment date" value={formatDateOnly(details.paymentDate)} />
                  ) : null}
                  {details.referenceNumber ? (
                    <DetailRow label="Reference number" value={details.referenceNumber} />
                  ) : null}
                  {details.notes ? <DetailRow label="Notes" value={details.notes} /> : null}
                  {recordedByName ? <DetailRow label="Recorded by" value={recordedByName} /> : null}
                  {details.recordedAt ? (
                    <DetailRow
                      label="Recorded on"
                      value={`${formatDate(details.recordedAt)} at ${formatTime(details.recordedAt)}`}
                    />
                  ) : null}
                </>
              )}

              {details.source === 'ONLINE' && (
                <>
                  <DetailRow label="Payment method" value="Online (Stripe)" />
                  {details.paidAt ? (
                    <DetailRow
                      label="Paid on"
                      value={`${formatDate(details.paidAt)} at ${formatTime(details.paidAt)}`}
                    />
                  ) : null}
                  {details.onlineStatus ? (
                    <DetailRow label="Status" value={details.onlineStatus} />
                  ) : null}
                </>
              )}

              {details.source === 'UNKNOWN' && details.paidAt ? (
                <DetailRow
                  label="Paid on"
                  value={`${formatDate(details.paidAt)} at ${formatTime(details.paidAt)}`}
                />
              ) : null}

              {details.source === 'UNKNOWN' ? (
                <p className="text-xs text-gray-500 pt-3">
                  Detailed payment records are not available for this deposit.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-gray-500 py-2">No deposit payment details found.</div>
          )}
        </div>

        <div className="flex items-center justify-end p-6 border-t border-gray-200 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepositPaymentDetailsModal;
